/**
 * GET /integrations/slack/channels — Slack channels the org's bot can post
 * to, filtered to public channels (or private channels the bot has joined)
 * and shaped as `Select` options. Shared by `NotificationsSection` and
 * `DigestsSection` so both admin config surfaces query the same data with
 * the same shape rather than each re-declaring the query and filter.
 *
 * `scope` distinguishes the two callers' query keys (`'notifications'` /
 * `'digests'`) so existing caching behaviour is unchanged.
 *
 * @module features/notifications/hooks/useSlackChannels
 */
import { useQuery } from '@tanstack/react-query';
import { API_URL } from '../../../config';

export type SlackChannel = { id: string; name: string; is_private?: boolean; is_member?: boolean };

export function useSlackChannels(scope: string, opts?: { enabled?: boolean }) {
  const query = useQuery({
    queryKey: ['slack-channels', scope],
    enabled: opts?.enabled ?? true,
    queryFn: async (): Promise<{ ok: boolean; channels?: SlackChannel[] }> => {
      const res = await fetch(`${API_URL}/integrations/slack/channels`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to load Slack channels');
      return res.json();
    },
  });

  const channelOptions =
    query.data?.channels
      ?.filter((channel) => !channel.is_private || channel.is_member)
      .map((channel) => ({ id: channel.id, label: `#${channel.name}` })) ?? [];

  return { ...query, channelOptions };
}
